import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EnvelopeSimple, PaperPlaneTilt, Phone, MapPin, IdentificationBadge } from '@phosphor-icons/react';
import { Container, Title, Text, Paper, Button, Stack, Group, Alert } from '@mantine/core';
import { CheckCircle } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api from '../utils/api';
import Turnstile from '../components/Turnstile';

export default function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [cfToken, setCfToken] = useState('');

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.message) {
      setError(t('contact.errorRequired'));
      return;
    }
    setSending(true);
    setError('');
    try {
      await api.post('/accounts/contact/', { ...formData, cf_turnstile_token: cfToken });
      setSent(true);
      setFormData({ name: '', email: '', message: '' });
    } catch {
      setError(t('contact.errorSend'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      <SEO title={t('contact.seo.title')} description={t('contact.seo.description')} path="/kontakt" />
      <Navbar />

      <section className="bg-[#F6F5F4] py-24">
        <Container size="xl">
          <Title
            order={1}
            ta="center"
            mb="xs"
           
            fz={{ base: 30, md: 48 }}
          >
            {t('contact.title')}
          </Title>
          <Text c="dimmed" ta="center" mb={48} maw={640} mx="auto">
            {t('contact.subtitle')}
          </Text>

          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {/* Contact info */}
            <div>
              <Title order={3} mb="lg">{t('contact.infoHeading')}</Title>
              <div className="space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <EnvelopeSimple size={24} weight="duotone" className="text-indigo-600" />
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">{t('contact.emailLabel')}</Text>
                    <a href="mailto:kontakt@droid.pl" className="text-xl text-gray-900 font-medium hover:text-indigo-600 transition">
                      kontakt@droid.pl
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <Phone size={24} weight="duotone" className="text-indigo-600" />
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">{t('contact.phoneLabel')}</Text>
                    <a href="tel:+48698685890" className="text-xl text-gray-900 font-medium hover:text-indigo-600 transition">
                      +48 698 685 890
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-6">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <MapPin size={24} weight="duotone" className="text-indigo-600" />
                </div>
                <div>
                  <Text size="sm" c="dimmed">{t('contact.addressLabel')}</Text>
                  <Text size="lg" fw={500} c="dark">{t('contact.address')}</Text>
                  <Text size="xs" c="dimmed">{t('contact.taxId')}</Text>
                </div>
              </div>

              <Paper radius="md" p="lg" withBorder mt="xl">
                <Text c="dimmed" lh={1.7}>
                  {t('contact.infoText')}
                </Text>
                <Text size="sm" fw={500} mt="md">
                  {t('contact.preferTalk')}{' '}
                  <a href="https://cal.eu/dariusz-stepniak/droid.pl-prezentacja" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2 transition">
                    {t('contact.bookDemo')}
                  </a>
                </Text>
              </Paper>
            </div>

            {/* Contact form */}
            <div>
              <Title order={3} mb="lg">{t('contact.formHeading')}</Title>
              {sent ? (
                <Paper radius="md" p="xl" withBorder ta="center">
                  <CheckCircle size={40} weight="fill" color="var(--mantine-color-green-5)" style={{ margin: '0 auto 12px' }} />
                  <Text size="lg" fw={600} mb={4}>{t('contact.sentTitle')}</Text>
                  <Text size="sm" c="dimmed">{t('contact.sentText')}</Text>
                  <Button variant="light" color="brand" mt="md" onClick={() => setSent(false)}>{t('contact.sendAnother')}</Button>
                </Paper>
              ) : (
              <form onSubmit={handleFormSubmit} className="space-y-4">
                {error && <Alert color="red" variant="light"><Text size="sm">{error}</Text></Alert>}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.nameLabel')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition"
                    placeholder={t('contact.namePlaceholder')}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.emailFieldLabel')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition"
                    placeholder={t('contact.emailPlaceholder')}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.messageLabel')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition resize-none"
                    placeholder={t('contact.messagePlaceholder')}
                  />
                </div>
                <Turnstile onToken={setCfToken} />
                <Button
                  type="submit"
                  fullWidth
                  radius="md"
                  size="md"
                  rightSection={<PaperPlaneTilt size={16} weight="fill" />}
                  loading={sending}
                >
                  {t('contact.submit')}
                </Button>
              </form>
              )}
            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
